import * as angular from 'angular';
import { assign, assignWith, debounce, defaults, partialRight, uniq, values } from 'lodash';

import { tokenFromVizID } from '@mode/charts/domain';
import { EmbedActionType, EmbedActions, EmbedReportState } from '@mode/reports/sandbox/contract';
import { ModelState } from '@mode/legacy/types-app-state';
import { dispatchEvent, isBuilderMode, isDesignerMode, isPhantomMode, isReportMode } from '@mode/shared/util-js';
import { DATA_LIMIT } from '../../../../common/constants';
import { PivotTableViewOptions } from '../../../../common/types';
import { numberTruncate } from '../../../../lib/axel/format';
import { locationSearchLookup } from '../../../../lib/location-search-lookup';
import FormatOptionsService from '../../../../services/format-options-service';
import ReportLinkService from '../../../../services/report-link-service';
import { pivotTableRenderer } from './pivot-table-renderer';
import SortModule from './sort-module';
import SortState, { SORT_ASC, SORT_DESC, SORT_NO_SORT, SortOrder, SortStack, Sorter } from './sort-state';

const TABLE_CELL_AREA_LIMIT = 50000;
const SINGLE_ROW_LIMIT = 2000;
const SQUARE_MAX_LIMIT = 150;
const CONTAINER_MASK_CLASS = 'pivot-table-container-mask';

export default class PivotTableController implements angular.IController {
  static $inject = ['$window', '$element', '$scope', '$timeout', 'ReportLinkService', 'FormatOptionsService'];
  view: any;
  dataset: EmbedReportState.EmbedDataset | ModelState;
  chartID: string;
  token: string;
  onAction: (args: { $action: EmbedActions }) => void;
  onEdit: (args: { $id: string; $view: any }) => void;
  onRenderStart: ($args: { $vizID: string }) => void;
  onRenderEnd: ($args: { $visualizationRendered: boolean }) => void;
  $container: any;
  isChartVisible: boolean;
  sortState = new SortState();
  plan: 'standard' | 'plus';
  message = {
    limit_exceeded: `Mode pivot tables will display up to ${numberTruncate()(DATA_LIMIT)} data points right now`,
    explore_info: 'Explore this pivot table to access the rest of the dataset on the fly.',
    warning_info: 'Keep pivot tables set to full width to allow exploring on your final report!',
  };
  CONTAINER_SELECTOR = '#js-pivot-table-container';
  SIDE_PANEL_SELECTOR = '#js-side-panel';
  contentArea: { rowCount: number; colCount: number } = { rowCount: 0, colCount: 0 };
  contentAreaLimit: number = TABLE_CELL_AREA_LIMIT;

  panelEnabled = false;
  inLightbox = false;
  hasChangedData = false;
  isClipboardEnabled = false; // handled by clipboard directive

  constructor(
    private $window,
    private $element,
    private $scope,
    private $timeout,
    private reportLinkService: ReportLinkService,
    private fos: FormatOptionsService
  ) {}

  $onInit() {
    this.token = tokenFromVizID(this.chartID);
    this.onRenderStart({ $vizID: this.token });

    if (!this.dataExceedsLimit()) {
      this.$container = this.$element.find(this.CONTAINER_SELECTOR);
      this.inLightbox = this.$container.closest('visualization-container').length > 0;
      this.$scope.$on('preview.update', this.handlePreviewUpdate.bind(this));
      // enable browser droppable interactions
      this.$element.on('dragover', (evt) => evt.preventDefault());
      this.renderTable();
    }

    const planFromUrl = locationSearchLookup(this.$window.location.search, 'report_owner_plan');
    this.plan = planFromUrl === 'plus' ? 'plus' : 'standard';
  }

  $onChanges(changed) {
    const viewDidChange = changed.view && !angular.equals(changed.view.previousValue, changed.view.currentValue);
    const datasetDidChange = changed.dataset;
    const containerIsAvailable = this.$container != null;
    const chartIsVisible = (changed.isChartVisible && changed.isChartVisible.currentValue) || this.isChartVisible;
    const canRenderTable = containerIsAvailable && chartIsVisible;
    const isFirstChange = changed.dataset && changed.dataset.isFirstChange();

    if (!this.dataExceedsLimit()) {
      if (viewDidChange || datasetDidChange) {
        this.initializeSortState();
        if (!isFirstChange) {
          this.hasChangedData = true;
        }
      }
      // the render fails if the table is hidden, so if we have changed data only update it when
      // it is put back into view
      if (canRenderTable && this.hasChangedData) {
        this.renderTable();
        this.hasChangedData = false;
      }
    }
  }

  $onDestroy = () => {
    this.$element.off('dragover');
    $(this.$window).off('resize.pivotWidth');
  };

  copySuccess() {
    this.onAction({
      $action: {
        type: EmbedActionType.ShowAlert,
        success: true,
        message: 'Link to pivot table copied successfully',
      },
    });
  }

  dataExceedsLimit() {
    return this.dataset != null && this.dataset.count >= DATA_LIMIT;
  }

  initializeSortState() {
    const { rows, columns } = this.pivotTableViewOptions();
    const attrs = uniq(rows.concat(columns));
    const type: 'axis' = 'axis';

    attrs.forEach((key) => {
      if (!this.sortState.has(key, type)) {
        // don't sort initially
        this.sortState.add({ key, type, order: SORT_NO_SORT });
      }
    });
  }

  /**
   * Initialize Sortable Headers
   *
   * Apply sort state to the ui state of sortable headers
   */
  initializeSortableHeaders() {
    const $table = this.$element.find('table');
    const $axisHeaders = $table.find('.pvtAxisLabel').not('.pvtNoSort');
    const defaultOrder = SORT_NO_SORT;
    const orderClassMap = {
      [SORT_ASC]: 'sort-asc',
      [SORT_DESC]: 'sort-desc',
      [SORT_NO_SORT]: '',
    };
    const orderReducer = (accu, { key, order }) => (accu[key] = order) && accu;
    const orderMap = this.sortState.filter({ type: 'axis' }).reduce(orderReducer, {});

    // clear classes from each header
    $axisHeaders.removeClass(values(orderClassMap).join(' '));
    // apply reduced sort state to header classes
    $axisHeaders.each(function () {
      const $header = $(this);
      const key = $header.data('axis-key');
      const order = orderMap[key] || defaultOrder;
      const orderClassName = orderClassMap[order];

      return $header.addClass(orderClassName);
    });
  }

  /**
   * Initialize Sortable Header Triggers
   */
  initializeSortableHeaderTogglers() {
    const togglerTemplate = `
      <a href="javascript:void(0)" class="axel-dropdown dropdown active">
        <span
          class="action-menu axel-dropdown-toggle dropdown-toggle"
          aria-haspopup="true"
          aria-expanded="false"></span>
      </a>
    `;
    const sortState = this.sortState;
    const renderTable = this.renderTable.bind(this);
    const $table = this.$element.find('table');
    const $allFixedHeaders = $table.find('.pvtAxisLabel .pvtFixedHeader').not('.pvtNoSort > .pvtFixedHeader');
    const $togglerPrototype = $(togglerTemplate);
    const createToggler = toggler($togglerPrototype);

    // add sort toggler to each header
    $allFixedHeaders.each(function addToggler(i, el) {
      $(el).append(
        createToggler({
          order: getSortState({ $el: $(el) }),
        })
      );
    });

    /**
     * Toggler
     *
     * factory method for toggle module
     * @param $prototype
     * @returns {({ order }:{order: any})=>Timer|null|number}
     */
    function toggler($prototype) {
      return function togglerFactory({ order }) {
        const $toggler = $prototype.clone();
        const sortIcon = SortModule.sortIcon(order);

        // bind click handler
        $toggler.on('click', handleTogglerClick);
        // set icon
        $toggler.find('.axel-dropdown-toggle').addClass(sortIcon);
        return $toggler;
      };
    }

    /**
     * Handle Trigger Click
     *
     * @param evt
     */
    function handleTogglerClick(evt) {
      const type = 'axis';
      const key = getKey({ $el: $(evt.currentTarget) });
      const order = SortModule.nextSortState(getSortState({ $el: $(evt.currentTarget) }));

      updateSortState(<Sorter>{ key, order, type });
    }

    /**
     * Get Sort State
     *
     * @todo this is brittle refactor how we get sort order from the $parent
     * @param $el
     * @returns {SortOrder}
     */
    function getSortState({ $el }): SortOrder {
      const $parent = $el.closest('td, th');
      const parentElem = $parent.get(0);
      const parentClass = parentElem && parentElem.className && parentElem.className.match(/sort-(.*)/);
      const sortState: string[] = [].slice.call(parentClass || []).slice(1);

      return (sortState && sortState.length && <SortOrder>sortState[0]) || SORT_NO_SORT;
    }

    /**
     * Get Key
     *
     * @param $el
     */
    function getKey({ $el }) {
      return $el.closest('th, td').first().data('axis-key');
    }

    /**
     * Update sort state and ui when ever sort event is triggered
     * @param key
     * @param order
     * @param type
     */
    function updateSortState({ key, order, type }: Sorter) {
      if (sortState.has(key, type)) {
        sortState.update(key, type, order);
      } else {
        sortState.add({ key, type, order });
      }
      renderTable();
    }
  }

  /**
   * Initialize Column Sort
   *
   * Init all sortable fixed headers
   */
  initializeColumnSort(): void {
    this.initializeSortableHeaders();
    this.initializeSortableHeaderTogglers();
  }

  dataState(pattern) {
    const state = this.dataset && this.dataset.state;

    if (state && pattern) {
      return !!~state.search(pattern);
    } else {
      return state;
    }
  }

  titleEditDisabled() {
    return this.isReportMode() && !this.isBuilderMode();
  }

  updateChartTitle(text) {
    this.view.chartTitle = text;
    this.broadcastEdit();
  }

  broadcastEdit() {
    this.onEdit({ $id: this.chartID, $view: this.view });
  }

  /**
   * Init Sticky Headers
   *
   * Bootstrap row, column, and axis headers to maintain their
   * layout position relative to vertical and horizontal scroll
   * positions.
   * @author franklin@modeanalytics.com
   * @todo consider breaking out into a seperate directive or jquery plugin
   * @todo consolidate heading/label and panel elements to itterable arrays
   *   - consolidate the position setting operations to methods that can operate on these arrays
   */
  initStickyHeaders(): void {
    const { rows, filters } = this.pivotTableViewOptions();
    const $thead = this.$element.find('thead');
    const $topPanel = this.$element.find('.pvtTopFixedHeaderPanel');
    const $leftPanel = this.$element.find('.pvtLeftFixedHeaderPanel');
    const $topLeftPanel = this.$element.find('.pvtTopLeftFixedHeaderPanel');
    const $axisLabels = this.$element.find('.pvtAxisLabel .pvtFixedHeader').not('.pvtFixedHeaderNoFollow');
    const $rowLabels = this.$element.find('th.pvtRowLabel .pvtFixedHeader');
    const $rowTotalLabel = this.$element.find('tbody .pvtTotalLabel .pvtFixedHeader');
    const $colLabels = this.$element.find('th.pvtColLabel .pvtFixedHeader');
    const $colKeyAxisLabel = this.$element.find('th.pvtAxisLabel[data-col-key][colspan] .pvtFixedHeader');
    const $rowKeyAxisLabels = this.$element.find('th.pvtAxisLabel[data-row-key] .pvtFixedHeader');
    const $colTotalLabel = this.$element.find('thead .pvtTotalLabel .pvtFixedHeader');
    const $mainHeaderWrapper = this.$element.find('.pvtMainHeaderWrapper');
    const $mainHeaderLabel = this.$element.find('.pvtMainHeaderContainer');
    const $table = this.$element.find('table');
    const $rowAndTotalLabels = $rowLabels.add($rowTotalLabel);
    const $colAndTotalLabels = $colLabels.add($colTotalLabel);
    let containerW = this.$container[0].offsetWidth;
    let tableH = $table.height();
    const rowLabelsSpan = () => {
      const $labels = rows.length ? $rowLabels.slice(0, rows.length) : $rowTotalLabel;

      return $labels
        .map(function () {
          return $(this).parent().get(0).offsetWidth;
        })
        .get()
        .reduce((a, b) => a + b, 0);
    };

    // main
    initHeaderLabels();
    initHeaderPanels();
    this.$container.on('scroll', handleContainerScroll);
    // trigger scroll to update header panel position
    this.$container.scroll();

    // prevent flicker and try to position axis labels
    initHeaderAxisLabels.call(this);

    // delay initialization to wait for scroll bars to appear
    setTimeout(() => {
      resampleDimensions.call(this);
      initHeaderAxisLabels.call(this);
      initHeaderPanels.call(this);
      this.$container.scroll();
    }, 100);

    /**
     * Resample Dimensions
     *
     * Since some rows and columns could shift or collapse we can
     * resample some dimensions
     */
    function resampleDimensions(): void {
      tableH = $table.height();
      containerW = this.$container.width();
    }

    /**
     * Init Header Panels
     *
     * Dynamically scale and position header background
     * panels. Set margin bottom to offset the panels
     * non-collapsed blank space left at the bottom after
     * the top position is set
     */
    function initHeaderPanels(): void {
      const leftPanelW = rowLabelsSpan();
      const topPanelH = $thead.height();
      const topPanelW = $thead.width();

      $leftPanel.css({
        top: `-${tableH}px`,
        height: `${tableH}px`,
        width: `${leftPanelW}px`,
        marginBottom: `-${tableH}px`,
      });
      $topPanel.css({
        top: `-${tableH}px`,
        height: `${topPanelH}px`,
        width: `${topPanelW}px`,
        marginBottom: `-${topPanelH}px`,
      });
      $topLeftPanel.css({
        top: `-${tableH}px`,
        height: `${topPanelH}px`,
        width: `${leftPanelW}px`,
        marginBottom: `-${topPanelH}px`,
      });
    }

    /**
     * Init Header Axis Labels
     *
     * Manually set the width of col axis and main header
     * labels and compensate for padding.
     */
    function initHeaderAxisLabels(width?): void {
      const cWidth = width || containerW;
      const leftPanelW = rowLabelsSpan();
      const scrollbarW = cWidth - this.$container.prop('clientWidth');
      const colKeyPaddingOffset = $colKeyAxisLabel.outerWidth() - $colKeyAxisLabel.width();
      const headerPaddingOffset = $mainHeaderLabel.outerWidth() - $mainHeaderLabel.width();
      let colKeyW = cWidth - (scrollbarW + leftPanelW);
      const mainHeaderWrapperW = cWidth - (scrollbarW + headerPaddingOffset);

      // @todo figure out why we need to do this only in designer mode
      if (this.isDesignerMode()) {
        colKeyW = colKeyW - colKeyPaddingOffset;
      }

      $mainHeaderWrapper.css({
        width: mainHeaderWrapperW,
      });

      $colKeyAxisLabel.css({
        width: colKeyW,
      });
    }

    function handleResize() {
      initHeaderAxisLabels.call(this, this.$container.width());
    }

    this.initPivotResizeHandler.call(this, handleResize);

    /**
     * Init Header Labels
     *
     * Read label container heights and set the label
     * element heights separately.  Note: doing this separately
     * is important as the layout can shift if you don't
     */
    function initHeaderLabels(): void {
      $axisLabels.not('.pvtNoSort .pvtFixedHeader').each(tagFilteredHeader);

      $rowAndTotalLabels.add($colAndTotalLabels).add($axisLabels).map(heightDefinition).get().forEach(updateHeight);

      function tagFilteredHeader(i, el) {
        const $el = $(el);
        const $headerContainer = $el.closest('.pvtAxisLabel');
        const axisKey = $headerContainer.data('axis-key');

        if (filters.indexOf(axisKey) >= 0) {
          $headerContainer.addClass('is-filtered');
        }
      }

      function heightDefinition(i, el) {
        const $this = $(el);
        const $container = $this.parent();
        const containerHeight = $container.height();

        return {
          $el: $this,
          containerHeight,
        };
      }

      function updateHeight({ $el, containerHeight }) {
        $el.css({
          height: `${containerHeight}px`,
        });
      }
    }

    /**
     * Handle Container Scroll
     *
     * Update header panel and heading positions on
     * next available animation frame
     *
     * @todo consider consolidating {{updateHeaderPositions}} into a single algorithm for all elements
     * @param evt
     */
    function handleContainerScroll(evt): void {
      const { scrollLeft, scrollTop } = evt.currentTarget;

      requestAnimationFrame(updateHeaderPositions);

      /**
       * Update Header Positions
       */
      function updateHeaderPositions() {
        // update header panel positions
        $leftPanel.css({
          left: scrollLeft,
        });
        $topPanel.css({
          top: -tableH + scrollTop,
        });
        $topLeftPanel.css({
          top: -tableH + scrollTop,
          left: scrollLeft,
        });

        // update header label positions
        $rowAndTotalLabels.each(function () {
          $(this).css({
            left: scrollLeft,
          });
        });
        $colAndTotalLabels.each(function () {
          $(this).css({
            top: scrollTop,
          });
        });
        $axisLabels.each(function () {
          $(this).css({
            top: scrollTop,
            left: scrollLeft,
          });
        });
      }
    }
  }

  initPivotResizeHandler(callback) {
    $(this.$window).on('resize.pivotWidth', debounce(callback.bind(this), 50));
  }

  handlePreviewUpdate(event, data) {
    if (data == null || (data.updateChart && data.updateChart.token === this.chartID)) {
      this.renderTable();
    }
  }

  renderTable() {
    if (this.dataset == null) {
      return;
    }

    const { rows, columns, values, aggregator } = this.pivotTableViewOptions();
    const $containerWrapper = this.$container.parent();
    const wrapperW = $containerWrapper.width();
    const containerW = this.$container.outerWidth();
    const clone = ($c) => $c.clone().addClass(CONTAINER_MASK_CLASS);
    const center = ($c) => $c.css({ left: (containerW - wrapperW) / -2 });

    if (this.$container.children().length) {
      // create and append table mask to cover transforming table
      $containerWrapper.append(center(clone(this.$container)));
    }

    const render = () => {
      this.$container.removeClass('reveal');
      this.$container.addClass('conceal');
      this.$container.off('scroll');
      this.$container
        .pivot(this.dataset.content, {
          rows: rows,
          cols: columns,
          vals: values,
          aggregator: this.pivotTableAggregator(aggregator),
          sorters: this.sortStateToSorters(),
          filter: this.pivotTableFilter(),
          renderer: pivotTableRenderer,
          rendererOptions: {
            beforeRender: ({ rowCount, colCount }) => {
              assign(this.contentArea, { rowCount, colCount });
              return !this.contentAreaExceedsLimit();
            },
          },
        })
        .append([
          $('<div class="pvtFixedHeaderPanel pvtLeftFixedHeaderPanel"></div>'),
          $('<div class="pvtFixedHeaderPanel pvtTopFixedHeaderPanel"></div>'),
          $('<div class="pvtFixedHeaderPanel pvtTopLeftFixedHeaderPanel"></div>'),
        ])
        .find('table:first-child')
        .each(() => {
          // no idea why but nesting two request animation frames
          // prevents a forced reflow which impacts performance
          this.$window.requestAnimationFrame(() => {
            this.$window.requestAnimationFrame(() => {
              this.initStickyHeaders.call(this);
              this.initializeColumnSort.call(this);
              // remove mask table
              $containerWrapper.find(`.${CONTAINER_MASK_CLASS}`).hide().remove();
              this.$container.removeClass('conceal');
              this.$container.addClass('reveal');
              this.onRenderEnd({ $visualizationRendered: true });
              this.$timeout(() => {
                // alert any resize handlers to resize pivot table now that its rendered
                dispatchEvent(this.$window, 'resize');
              });
            });
          });
        });
    };

    this.$window.requestAnimationFrame(render);
  }

  /**
   * @returns {PivotTableViewOptions|{}}
   */
  pivotTableViewOptions(): PivotTableViewOptions {
    return defaults({}, this.view.pivotTable, {
      filters: [],
      filterValues: {},
      columnFormats: {},
      columns: [],
      rows: [],
      values: [],
    });
  }

  /**
   * @todo replace temporary aggregator with dynamic one
   * @returns {any}
   */
  pivotTableAggregator(aggName) {
    const { sum, count, countUnique, average, max, min } = (<any>$).pivotUtilities.aggregatorTemplates;
    const { values } = this.pivotTableViewOptions();
    const firstValue = [values[0]];
    const formatter = this.getFormatter();
    const aggregatorMap = {
      DEFAULT: () => aggregatorMap['count'](),
      sum: () => sum(formatter)(firstValue),
      count: () => count(formatter)(),
      countDistinct: () => countUnique(formatter)(firstValue),
      average: () => average(formatter)(firstValue),
      max: () => max(formatter)(firstValue),
      min: () => min(formatter)(firstValue),
    };

    return (aggregatorMap[aggName] || aggregatorMap['DEFAULT'])();
  }

  /**
   * Sort State to Sorters
   *
   * Reduce sort state to a PivotTablesJS sorters object
   * and return a getter
   * @todo upgrade PivotTableJS so we don't need to return a getter
   * @returns {(attr:any)=>(a:any, b:any)=>any}
   */
  sortStateToSorters() {
    const { naturalSort } = (<any>$).pivotUtilities;
    const naturalSortDesc = function (a, b) {
      return naturalSort.call(null, b, a);
    };
    const sortMap = {
      [SORT_ASC]: naturalSort,
      [SORT_DESC]: naturalSortDesc,
      [SORT_NO_SORT]: naturalSort,
    };
    const sorters: SortStack = this.sortState.getStack();
    const reducedSorters: { [colKey: string]: (a, b) => any } = sorters.reduce(sorterReducer, {});

    function sorterReducer(accu, prev: Sorter) {
      accu[prev.key] = sortMap[String(prev.order)];
      return accu;
    }

    return reducedSorters;
  }

  /**
   * @returns {(record:any)=>boolean}
   */
  pivotTableFilter() {
    return (record) => {
      const { filterValues } = this.pivotTableViewOptions();
      const inFilterValues = (column) => filterValues[column].indexOf(record[column]) >= 0;
      const result = !Object.keys(filterValues).some(inFilterValues);

      return result;
    };
  }

  containerHeight() {
    return this.$element.find(this.SIDE_PANEL_SELECTOR).height();
  }

  isDesignerMode() {
    return isDesignerMode(this.$window.document.body);
  }

  isReportMode() {
    return isReportMode(this.$window.document.body);
  }

  isBuilderMode() {
    return isBuilderMode(this.$window.document.body);
  }

  isPhantomMode() {
    return isPhantomMode(this.$window);
  }

  /**
   * Is Empty State In Designer
   *
   * Derivative state that depends on the absence of mapped pivot config
   * attributes and the designer mode context.
   * @returns {boolean}
   */
  isEmptyStateInDesigner() {
    const { rows, columns, values, filters } = this.pivotTableViewOptions();
    const opts = [...rows, ...columns, ...values, ...filters];

    return !opts.length && this.isDesignerMode();
  }

  linkTo($event, type) {
    if (this.reportLinkService.isLinkable()) {
      $event.preventDefault();
      $event.stopPropagation();
      this.reportLinkService.linkToPage(
        type === 'lightbox' || type === 'explore' ? { id: this.chartID, view: this.view } : this.queryToken(),
        type
      );
    }
  }

  getReportDataURL() {
    const data = this.getQueryData();
    return (data && data['reportDataUrl']) || '';
  }

  getReportQueryURL() {
    const data = this.getQueryData();
    return (data && data['reportQueryUrl']) || '';
  }

  getReportVizURL() {
    return (this.view && this.view._vizReportRunLink) || '';
  }

  queryToken() {
    return this.dataset && this.dataset.query_token;
  }

  queryName() {
    const queryData = this.getQueryData();
    const queryName = queryData && queryData['queryName'];

    return queryName || '';
  }

  getDOMClasses() {
    return {
      'is-design-mode': this.isDesignerMode(),
      'is-phantom-mode': this.isPhantomMode(),
      'is-report-mode': this.isReportMode(),
      'is-panel-open': this.panelEnabled,
      'no-row-dividers': ((this.view.pivotTable && this.view.pivotTable.rows) || []).length <= 1,
      'no-col-dividers': ((this.view.pivotTable && this.view.pivotTable.columns) || []).length <= 1,
    };
  }

  /**
   * @todo refactor getFormatter to not require a colkey
   * @returns {any}
   */
  getFormatter() {
    let formatter;
    const { columnFormats, values } = this.pivotTableViewOptions();
    const colKey = values[0];
    const format = columnFormats[colKey] || {};
    const colType = format.type !== 'automatic' ? format.type : 'number';
    const moneyFormatters = this.fos.getPresetFormatters(columnFormats);

    formatter = this.fos.getFormatter(columnFormats, colKey, null, colType, moneyFormatters);

    return formatter;
  }

  getQueryData() {
    return this.dataset;
  }

  togglePanel($event) {
    this.linkTo($event, 'explore');
  }

  openPanel() {
    this.panelEnabled = true;
  }

  closePanel() {
    this.panelEnabled = false;
  }

  updateView(view: PivotTableViewOptions) {
    this.view.pivotTable = view;
  }

  /**
   * Content Area Exceeds Limit
   *
   * @author <benn@modeanalytics.com>, <franklin@modeanalytics.com>.. but mostly benn!
   * @returns {boolean}
   */
  contentAreaExceedsLimit() {
    const defaults: any = partialRight(assignWith, (defaultVal, srcVal) => (srcVal > 0 ? srcVal : defaultVal));
    const { rowCount, colCount } = defaults(
      {
        rowCount: 1,
        colCount: 1,
      },
      this.contentArea
    );
    const singleMax = SINGLE_ROW_LIMIT;
    const squareMax = SQUARE_MAX_LIMIT;
    const areaMax = squareMax * squareMax;
    const multiplierBound = areaMax / singleMax;
    const base = Math.pow(singleMax, 1 / multiplierBound);
    const numer = Math.max(rowCount, colCount);
    const denom = Math.min(rowCount, colCount);
    const ratio = numer / denom;
    const multiplier = 1 + Math.log(ratio) / Math.log(base);
    const adjustedArea = rowCount * colCount * multiplier;

    return adjustedArea > areaMax;
  }
}
